"use client"

import { useTranslations } from "next-intl"
import { ArrowUpRight, Globe } from "lucide-react"
import {
  siFacebook,
  siIndeed,
  siInstagram,
  siSnapchat,
  siThreads,
  siTiktok,
  siX,
  siYoutube,
  type SimpleIcon,
} from "simple-icons"

// The "also posted here" link on a role. Owners paste the IG post, TikTok,
// flyer or shop site they announced the opening on (hiring-form's
// original_announcement_url); this turns that URL into a small brand mark
// plus "Also on Instagram", the way job boards credit a cross-post.
//
// Known hosts get their mark from simple-icons, drawn in the text colour so
// it sits in the line like a glyph rather than a logo sticker. Anything else
// (the shop's own site, a Linktree) gets a globe and the generic label: a
// brand mark we'd have to guess at is worse than none.

type Source = { icon: SimpleIcon; name: string }

const SOURCES: Array<{ match: RegExp; source: Source }> = [
  { match: /(^|\.)instagram\.com$/, source: { icon: siInstagram, name: "Instagram" } },
  { match: /(^|\.)(facebook|fb)\.com$/, source: { icon: siFacebook, name: "Facebook" } },
  { match: /(^|\.)tiktok\.com$/, source: { icon: siTiktok, name: "TikTok" } },
  { match: /(^|\.)(x|twitter)\.com$/, source: { icon: siX, name: "X" } },
  { match: /(^|\.)threads\.(net|com)$/, source: { icon: siThreads, name: "Threads" } },
  { match: /(^|\.)(youtube\.com|youtu\.be)$/, source: { icon: siYoutube, name: "YouTube" } },
  { match: /(^|\.)snapchat\.com$/, source: { icon: siSnapchat, name: "Snapchat" } },
  { match: /(^|\.)indeed\.com$/, source: { icon: siIndeed, name: "Indeed" } },
]

export function detectAnnouncementSource(url: string): Source | null {
  let host: string
  try {
    host = new URL(url).hostname.toLowerCase().replace(/^www\./, "")
  } catch {
    return null
  }
  return SOURCES.find((s) => s.match.test(host))?.source ?? null
}

export function AnnouncementLink({ url, className = "" }: { url: string; className?: string }) {
  const t = useTranslations("pages.hiring")
  const source = detectAnnouncementSource(url)

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`text-sm text-foreground hover:underline underline-offset-4 inline-flex items-center gap-1.5 ${className}`}
    >
      {source ? (
        <svg
          viewBox="0 0 24 24"
          width={14}
          height={14}
          fill="currentColor"
          aria-hidden
          className="flex-shrink-0 opacity-80"
        >
          <path d={source.icon.path} />
        </svg>
      ) : (
        <Globe className="w-3.5 h-3.5 flex-shrink-0 opacity-80" strokeWidth={1.75} aria-hidden />
      )}
      {source ? t("detail.alsoOn", { source: source.name }) : t("detail.viewOriginalAnnouncement")}
      {/* lucide ArrowUpRight, like every other out-link in the product —
          the ↗ glyph this used renders at a different weight and baseline in
          every font it lands in. */}
      <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0 opacity-70" strokeWidth={1.75} aria-hidden />
    </a>
  )
}
